// The single door to the Worker. Everything the app does goes through here, so
// identity, the surface header, and error shape are decided in exactly one place.
//
// Two things every request carries:
//   • Authorization: Bearer <session>  — the account. Web has no RevenueCat id,
//     which identity.ts already expects ("X-App-User-Id is optional (web has none)").
//   • X-Client-Surface: web            — lets the Worker apply the tighter web
//     rate limits and tag analytics by origin. Not a security boundary: it only
//     ever costs the caller headroom.

export const API_BASE =
  import.meta.env.VITE_WORKER_URL || "https://holyshot-worker.joel-a24.workers.dev";

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number,
  ) {
    super(message);
  }
}

const TOKEN_KEY = "hs.token";
const EMAIL_KEY = "hs.email";

export const session = {
  get token() {
    return localStorage.getItem(TOKEN_KEY);
  },
  get email() {
    return localStorage.getItem(EMAIL_KEY);
  },
  get signedIn() {
    return !!localStorage.getItem(TOKEN_KEY);
  },
  save(token: string, email: string) {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(EMAIL_KEY, email);
  },
  clear() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EMAIL_KEY);
  },
};

function headers(extra: Record<string, string> = {}): Record<string, string> {
  const h: Record<string, string> = { "X-Client-Surface": "web", ...extra };
  const t = session.token;
  if (t) h.Authorization = `Bearer ${t}`;
  return h;
}

/**
 * A failed fetch rejects with a raw TypeError ("Failed to fetch", "Load failed"
 * — wording differs per browser). That is not something a user should ever read,
 * so every network path funnels through here and comes out as an ApiError like
 * any other failure.
 */
async function send(input: string, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch {
    throw new ApiError("network", "We couldn't reach HolyShot. Check your connection and try again.", 0);
  }
}

async function parse<T>(res: Response): Promise<T> {
  const body = await res.json().catch(() => ({}) as Record<string, unknown>);
  if (!res.ok) {
    const b = body as { error?: string; message?: string };
    // A dead session should log you out rather than loop on 401s.
    if (res.status === 401 && session.signedIn) session.clear();
    throw new ApiError(b.error || "error", b.message || "Something went wrong. Please try again.", res.status);
  }
  return body as T;
}

export async function get<T>(path: string): Promise<T> {
  return parse<T>(await send(`${API_BASE}${path}`, { headers: headers() }));
}

export async function post<T>(path: string, body?: unknown): Promise<T> {
  return parse<T>(
    await send(`${API_BASE}${path}`, {
      method: "POST",
      headers: headers({ "Content-Type": "application/json" }),
      body: JSON.stringify(body ?? {}),
    }),
  );
}

/** Multipart (selfie uploads). Never set Content-Type — the browser adds the boundary. */
export async function postForm<T>(path: string, form: FormData): Promise<T> {
  return parse<T>(await send(`${API_BASE}${path}`, { method: "POST", headers: headers(), body: form }));
}

// ── Types mirrored from the Worker ──────────────────────────────────

export interface CreditsView {
  earned: number;
  redeemed: string[];
  available: number;
  /** All-access: generation isn't gated on the credit count. The server tells us
   *  because the web has no RevenueCat SDK to read the entitlement from. */
  unlimited?: boolean;
}
export interface AuthVerifyResponse {
  token: string;
  userId: string;
  email: string;
  isNew: boolean;
  credits?: CreditsView;
}
export interface WebPack {
  cents: number;
  credits: number;
  bonus: number;
  label: string;
}
export interface LibraryPortrait {
  promptId: string;
  label: string;
  url: string;
  key?: string;
}
export interface LibraryEra {
  packId: string;
  title: string;
  portraits: LibraryPortrait[];
}
export interface LibraryResponse {
  eras: LibraryEra[];
  clips: string[];
  reels: string[];
}
export interface EraDTO {
  id: string;
  title: string;
  blurb: string;
  coverImage: string;
  coverImageURL?: string | null;
  promptCount: number;
}
export interface ErasResponse {
  eras: EraDTO[];
  comingNext?: { title: string; month: string } | null;
}

/**
 * A cover URL the web can actually load.
 *
 * `coverImageURL` is only populated for eras whose art ISN'T bundled in the apps.
 * The original nine ship `coverImage` as an asset NAME ("cover_babylon") that the
 * app resolves from its own bundle — the web has no bundle, so it rendered
 * nothing. The Worker serves the same art from R2, so fall back to that.
 */
export function eraCoverUrl(era: Pick<EraDTO, "id" | "coverImage" | "coverImageURL">): string | null {
  if (era.coverImageURL) return era.coverImageURL;
  if (!era.coverImage) return null;
  return `${API_BASE}/v1/era-art/${encodeURIComponent(era.id)}/${encodeURIComponent(era.coverImage)}`;
}
export interface ConsentStatus {
  accepted: boolean;
  version: string;
}
export interface AnimateBalance {
  earned: number;
  consumed: number;
  available: number;
}

// ── Calls ───────────────────────────────────────────────────────────

export const api = {
  // auth
  requestCode: (email: string) => post<{ ok: boolean }>("/v1/auth/request-code", { email, source: "web2app" }),
  verifyCode: (email: string, code: string) => post<AuthVerifyResponse>("/v1/auth/verify", { email, code }),
  signOut: () => post<{ ok: boolean }>("/v1/auth/signout"),
  me: () => get<{ signedIn: boolean; email?: string; credits?: CreditsView }>("/v1/auth/me"),

  // credits
  credits: () => get<CreditsView>("/v1/credits"),
  packs: () => get<{ packs: Record<string, WebPack> }>("/v1/web/packs"),
  checkout: (packKey: string, steerToken?: string) =>
    post<{ url: string; context: string; credits: number }>("/v1/web/checkout", { packKey, steerToken }),
  redeem: (packId: string) => post<{ packId: string; credits: CreditsView }>("/v1/redeem", { packId }),

  // catalog + consent
  eras: () => get<ErasResponse>("/v1/eras"),
  consent: () => get<ConsentStatus>("/v1/consent"),
  acceptConsent: () => post<ConsentStatus>("/v1/consent"),

  // library
  library: () => get<LibraryResponse>("/v1/library"),
  deleteLibrary: (keys: string[]) => post<{ ok: boolean; deleted: number }>("/v1/library/delete", { keys }),

  // Bring to Life — clips come from IAP (app) or Stripe (web); the server sums both.
  animateBalance: () => get<AnimateBalance>("/v1/animate/balance"),
  animate: (portraitUrl: string, packId: string, portraitId: string) =>
    post<{ url: string; balance: AnimateBalance }>("/v1/animate", { portraitUrl, packId, portraitId }),

  // reporting
  report: (type: string, targetRef?: string, reason?: string) =>
    post<{ ok: boolean; id: string }>("/v1/report", { type, targetRef, reason }),
};
